import { useState, useEffect, useCallback } from 'react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card'
import { Progress } from '@/components/ui/progress'
import { 
  X, 
  ChevronLeft, 
  ChevronRight, 
  Upload, 
  Play, 
  Camera,
  Download,
  Sparkles
} from 'lucide-react'
import { cn } from '@/lib/utils'

interface TourStep {
  id: number
  title: string
  description: string
  icon: React.ReactNode
  target?: string // CSS selector for the element to highlight
  position?: 'top' | 'bottom' | 'left' | 'right' | 'center'
}

const tourSteps: TourStep[] = [
  {
    id: 1,
    title: "Upload Your Video",
    description: "Start by uploading a video file. We support MP4, WebM, MOV and more. Just drag and drop or click to browse.",
    icon: <Upload className="h-8 w-8" />,
    position: 'center',
  },
  {
    id: 2,
    title: "Navigate Frame by Frame",
    description: "Use the timeline to scrub through your video, or use arrow keys for precise frame-by-frame navigation.",
    icon: <Play className="h-8 w-8" />,
    position: 'center',
  },
  {
    id: 3,
    title: "Capture Perfect Frames",
    description: "Found the perfect moment? Click 'Capture Frame' or press 'C' to save it to your library.",
    icon: <Camera className="h-8 w-8" />,
    position: 'center',
  },
  {
    id: 4,
    title: "Export in High Quality",
    description: "Download your frames as PNG or JPEG. Create GIFs, or save to your cloud library for access anywhere.",
    icon: <Download className="h-8 w-8" />,
    position: 'center',
  },
]

interface OnboardingTourProps {
  onComplete: () => void
  onSkip: () => void
}

export function OnboardingTour({ onComplete, onSkip }: OnboardingTourProps) {
  const [currentStep, setCurrentStep] = useState(0)
  const [isAnimating, setIsAnimating] = useState(false)

  const step = tourSteps[currentStep]
  const progress = ((currentStep + 1) / tourSteps.length) * 100
  const isLastStep = currentStep === tourSteps.length - 1

  const goToNext = useCallback(() => {
    if (isAnimating) return
    
    setIsAnimating(true)
    setTimeout(() => {
      if (isLastStep) {
        onComplete()
      } else {
        setCurrentStep(prev => prev + 1)
      }
      setIsAnimating(false)
    }, 300)
  }, [isLastStep, onComplete, isAnimating])

  const goToPrev = useCallback(() => {
    if (isAnimating || currentStep === 0) return
    
    setIsAnimating(true)
    setTimeout(() => {
      setCurrentStep(prev => prev - 1)
      setIsAnimating(false)
    }, 300)
  }, [currentStep, isAnimating])

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight' || e.key === 'Enter') {
        goToNext()
      } else if (e.key === 'ArrowLeft') {
        goToPrev()
      } else if (e.key === 'Escape') {
        onSkip()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [goToNext, goToPrev, onSkip])

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-background/80 backdrop-blur-sm"
        onClick={onSkip}
      />

      {/* Tour Card */}
      <Card className={cn(
        "relative w-full max-w-lg mx-4 shadow-2xl",
        "transition-all duration-300",
        isAnimating && "opacity-50 scale-95"
      )}>
        {/* Close button */}
        <Button
          variant="ghost"
          size="icon"
          className="absolute right-2 top-2 text-muted-foreground hover:text-foreground"
          onClick={onSkip}
        >
          <X className="h-4 w-4" />
        </Button>

        <CardHeader className="text-center pb-4">
          {/* Progress */}
          <div className="mb-6">
            <Progress value={progress} className="h-1" />
            <p className="text-xs text-muted-foreground mt-2">
              Step {currentStep + 1} of {tourSteps.length}
            </p>
          </div>

          {/* Icon */}
          <div className="w-20 h-20 mx-auto rounded-full bg-primary/10 flex items-center justify-center text-primary mb-4">
            {step.icon}
          </div>

          <CardTitle className="text-2xl">{step.title}</CardTitle>
        </CardHeader>

        <CardContent className="text-center pb-4">
          <p className="text-muted-foreground text-lg leading-relaxed">
            {step.description}
          </p>
        </CardContent>

        <CardFooter className="flex items-center justify-between gap-4 pt-4">
          <Button
            variant="ghost"
            onClick={goToPrev}
            disabled={currentStep === 0}
            className="gap-1"
          >
            <ChevronLeft className="h-4 w-4" />
            Back
          </Button>

          {/* Step dots */}
          <div className="flex gap-2">
            {tourSteps.map((_, index) => (
              <div
                key={index}
                className={cn(
                  "w-2 h-2 rounded-full transition-all duration-300",
                  index === currentStep
                    ? "bg-primary w-6"
                    : index < currentStep
                    ? "bg-primary/50"
                    : "bg-muted"
                )}
              />
            ))}
          </div>

          <Button onClick={goToNext} className="gap-1">
            {isLastStep ? (
              <>
                Get Started
                <Sparkles className="h-4 w-4" />
              </>
            ) : (
              <>
                Next
                <ChevronRight className="h-4 w-4" />
              </>
            )}
          </Button>
        </CardFooter>

        {/* Skip link */}
        <div className="text-center pb-4">
          <button
            onClick={onSkip}
            className="text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            Skip tour
          </button>
        </div>
      </Card>
    </div>
  )
}
